import React, { FC } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  OnboardingDraft,
  SportTypeName,
  ValidationIssue,
} from '@intelligent-play-v2/domain';

import { Button, ButtonType } from '~/components/button';
import { TextInput } from '~/components/forms';
import { LabelledCheckbox } from '~/components/labelledCheckbox';

import { OnboardingMaintenanceSchedules } from './OnboardingMaintenanceSchedules';
import { OnboardingSensorRow } from './OnboardingSensorRow';
import {
  NaturalSize,
  PitchCentrePicker,
  SiblingCentre,
} from './PitchCentrePicker';
import {
  fieldStateFor,
  inputStateClass,
  issuesUnder,
  messageAt,
} from './fieldState';
import { useDraftField } from './useDraftField';

/** The operating window is stored per day, even though it is edited as one. */
const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

/** The sports the database recognises; anything else cannot be recorded. */
const SPORTS: SportTypeName[] = [
  'Soccer',
  'American Football',
  'Hockey',
  'Rugby',
  'AFL',
  'Cricket',
  'Lacrosse',
];

interface OnboardingPitchCardProps {
  pitchIndex: number;
  issues: ValidationIssue[];
  templateSignatures: string[];
  /** The overview image, once the reviewer has chosen one; the centre is marked on it. */
  imageUrl: string | null;
  /** The other pitches' marked centres, for orientation. */
  siblingCentres: SiblingCentre[];
}

export const OnboardingPitchCard: FC<OnboardingPitchCardProps> = ({
  pitchIndex,
  issues,
  templateSignatures,
  imageUrl,
  siblingCentres,
}) => {
  const {
    control,
    register,
    watch,
    setValue,
  } = useFormContext<OnboardingDraft>();
  const base = `pitches.${pitchIndex}` as const;
  const pitch = watch(`pitches.${pitchIndex}`);

  const {
    fields: sensorFields,
    append: appendSensor,
    remove: removeSensor,
  } = useFieldArray({ control, name: `pitches.${pitchIndex}.sensors` });

  /*
   * Everything with no `<input>` behind it goes through useDraftField: chips, checkboxes
   * and the add/remove lists only reach the committed draft if the name is registered.
   */
  const [sports, setSports] = useDraftField(`${base}.sports` as const);
  const [snapshotTimes, setSnapshotTimes] = useDraftField(
    `${base}.snapshotTimes` as const
  );
  const [artificiallyLit, setArtificiallyLit] = useDraftField(
    `${base}.artificiallyLit` as const
  );
  const [outOfHours, setOutOfHours] = useDraftField(
    `${base}.outOfHours` as const
  );

  /*
   * One window, written to all seven days. The card offers a single pair of inputs, so
   * anything it cannot reach has to be kept in step with what it can — otherwise a
   * validation error lands on a day with no control behind it and nothing can clear it,
   * or the reviewer corrects Monday and ships six days of wrong alerting.
   */
  const operatingWindow = outOfHours?.monday ?? { start: '', end: '' };
  const setOperatingWindow = (edge: 'start' | 'end', value: string): void => {
    const days = Object.keys(outOfHours ?? {});
    setOutOfHours(
      Object.fromEntries(
        (days.length > 0 ? days : DAYS).map(day => [
          day,
          { ...operatingWindow, ...outOfHours?.[day], [edge]: value },
        ])
      )
    );
  };
  const windowIssues = issuesUnder(issues, `${base}.outOfHours`);

  /*
   * The centre inputs are registered below, so setValue on their names is enough — the
   * click on the image and the typed number are two ways of editing the same two fields.
   */
  const setCentre = (point: { x: number; y: number }): void => {
    setValue(`${base}.pitchCenterPixels.x` as const, point.x, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(`${base}.pitchCenterPixels.y` as const, point.y, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  /*
   * The centre is clicked in the image's natural pixel grid, so the size has to be the
   * same grid or the dashboard overlay lands in the wrong place. Filling it from the
   * loaded image keeps the two in step without the reviewer having to retype it.
   */
  const setSizeFromImage = (size: NaturalSize): void => {
    setValue(`${base}.pitchSizePixels.x` as const, size.width, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(`${base}.pitchSizePixels.y` as const, size.height, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const toggleSport = (sport: SportTypeName): void => {
    setSports(
      sports.includes(sport)
        ? sports.filter(existing => existing !== sport)
        : [...sports, sport]
    );
  };

  return (
    <div className="mt-8 rounded border border-primary-250 bg-primary-100 p-5">
      <p className="text-xl font-semibold">
        Pitch {pitchIndex + 1}
        {pitch?.name ? ` — ${pitch.name}` : ''}
      </p>

      <section className="mt-4">
        <p className="text-lg font-semibold">Identity</p>
        <div className="mt-2 grid grid-cols-1 gap-4 lg:grid-cols-4">
          <TextInput
            label="Pitch ID"
            isLightMode
            required
            register={register(`${base}.id` as const, { valueAsNumber: true })}
            className={inputStateClass(fieldStateFor(issues, `${base}.id`))}
            errorMessage={messageAt(issues, `${base}.id`)}
          />
          <TextInput
            label="Name"
            isLightMode
            required
            register={register(`${base}.name` as const)}
            className={inputStateClass(fieldStateFor(issues, `${base}.name`))}
            errorMessage={messageAt(issues, `${base}.name`)}
          />
          <TextInput
            label="Pitch number in facility"
            isLightMode
            type="number"
            register={register(`${base}.facilityPitchNumber` as const, {
              valueAsNumber: true,
            })}
          />
          <TextInput
            label="PitchFinder pitch ID"
            isLightMode
            type="number"
            register={register(`${base}.pitchFinderId` as const, {
              setValueAs: value => (value === '' ? null : Number(value)),
            })}
          />
        </div>
      </section>

      <section className="mt-6">
        <p className="text-lg font-semibold">Sports</p>
        <p className="text-sm text-primary-500">
          Only these are recognised by the database. The stored usage string is
          built from what is selected here.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {SPORTS.map(sport => {
            const isSelected = sports.includes(sport);
            return (
              <button
                key={sport}
                type="button"
                onClick={() => toggleSport(sport)}
                className={`rounded-full border px-3 py-1 text-sm font-semibold ${
                  isSelected
                    ? 'border-green bg-green-lightest text-green-dark'
                    : 'border-primary-250 bg-white text-primary-600'
                }`}
              >
                {sport}
              </button>
            );
          })}
        </div>
        {(pitch?.unrecognisedSports ?? []).length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-sm text-primary-500">
              Not recognised, so it will not be recorded:
            </span>
            {(pitch?.unrecognisedSports ?? []).map(sport => (
              <span
                key={sport}
                className="rounded-full border border-yellow bg-yellow-lightest px-3 py-1 text-sm font-semibold text-yellow"
              >
                {sport}
              </span>
            ))}
          </div>
        )}
      </section>

      <section className="mt-6">
        <p className="text-lg font-semibold">Location and size</p>
        <div className="mt-2 grid grid-cols-1 gap-4 lg:grid-cols-4">
          <TextInput
            label="Latitude"
            isLightMode
            register={register(`${base}.latLong.0` as const, {
              valueAsNumber: true,
            })}
            className={inputStateClass(
              fieldStateFor(issues, `${base}.latLong`)
            )}
            errorMessage={messageAt(issues, `${base}.latLong`)}
          />
          <TextInput
            label="Longitude"
            isLightMode
            register={register(`${base}.latLong.1` as const, {
              valueAsNumber: true,
            })}
            className={inputStateClass(
              fieldStateFor(issues, `${base}.latLong`)
            )}
          />
          <TextInput
            label="Length (m)"
            isLightMode
            type="number"
            register={register(`${base}.dimensionsMeters.x` as const, {
              valueAsNumber: true,
            })}
            className={inputStateClass(
              fieldStateFor(issues, `${base}.dimensionsMeters.x`)
            )}
            errorMessage={messageAt(issues, `${base}.dimensionsMeters.x`)}
          />
          <TextInput
            label="Width (m)"
            isLightMode
            type="number"
            register={register(`${base}.dimensionsMeters.y` as const, {
              valueAsNumber: true,
            })}
            className={inputStateClass(
              fieldStateFor(issues, `${base}.dimensionsMeters.y`)
            )}
            errorMessage={messageAt(issues, `${base}.dimensionsMeters.y`)}
          />
        </div>
      </section>

      <section className="mt-6">
        <p className="text-lg font-semibold">Image spec</p>
        <p className="text-sm text-primary-500">
          {imageUrl
            ? 'Click the centre of this pitch on the overview image, or type the pixel coordinates. '
            : 'Pitch centre is measured off the facility overview image — choose the image at the top of the page to mark it by clicking. '}
          {imageUrl
            ? 'The size is taken from the image.'
            : 'The size defaults to 1280×800 until an image is chosen.'}
        </p>
        {imageUrl && (
          <div className="mt-3">
            <PitchCentrePicker
              imageUrl={imageUrl}
              value={pitch?.pitchCenterPixels ?? { x: null, y: null }}
              onChange={setCentre}
              onImageLoad={setSizeFromImage}
              siblings={siblingCentres}
            />
          </div>
        )}
        <div className="mt-2 grid grid-cols-1 gap-4 lg:grid-cols-4">
          <TextInput
            label="Centre X (px)"
            isLightMode
            type="number"
            register={register(`${base}.pitchCenterPixels.x` as const, {
              setValueAs: value => (value === '' ? null : Number(value)),
            })}
            required
            className={inputStateClass(
              fieldStateFor(issues, `${base}.pitchCenterPixels.x`)
            )}
            errorMessage={messageAt(issues, `${base}.pitchCenterPixels.x`)}
          />
          <TextInput
            label="Centre Y (px)"
            isLightMode
            type="number"
            register={register(`${base}.pitchCenterPixels.y` as const, {
              setValueAs: value => (value === '' ? null : Number(value)),
            })}
            required
            className={inputStateClass(
              fieldStateFor(issues, `${base}.pitchCenterPixels.y`)
            )}
            errorMessage={messageAt(issues, `${base}.pitchCenterPixels.y`)}
          />
          <TextInput
            label="Size X (px)"
            isLightMode
            type="number"
            register={register(`${base}.pitchSizePixels.x` as const, {
              valueAsNumber: true,
            })}
            className={inputStateClass(
              fieldStateFor(issues, `${base}.pitchSizePixels.x`)
            )}
            errorMessage={messageAt(issues, `${base}.pitchSizePixels.x`)}
          />
          <TextInput
            label="Size Y (px)"
            isLightMode
            type="number"
            register={register(`${base}.pitchSizePixels.y` as const, {
              valueAsNumber: true,
            })}
            className={inputStateClass(
              fieldStateFor(issues, `${base}.pitchSizePixels.y`)
            )}
            errorMessage={messageAt(issues, `${base}.pitchSizePixels.y`)}
          />
        </div>
      </section>

      <section className="mt-6">
        <p className="text-lg font-semibold">Turf</p>
        <div className="mt-2 grid grid-cols-1 gap-4 lg:grid-cols-4">
          <TextInput
            label="Type"
            isLightMode
            register={register(`${base}.turfType` as const)}
          />
          <TextInput
            label="Manufacturer"
            isLightMode
            register={register(`${base}.turfManufacturer` as const)}
          />
          <TextInput
            label="Product"
            isLightMode
            register={register(`${base}.turfProduct` as const)}
          />
          <div className="flex items-end">
            <LabelledCheckbox
              title="Artificially lit"
              isChecked={artificiallyLit}
              toggleChecked={() => setArtificiallyLit(!artificiallyLit)}
            />
          </div>
        </div>
      </section>

      <section className="mt-6">
        <p className="text-lg font-semibold">Alerts</p>
        <div className="mt-2 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <TextInput
            label="Benchmark players"
            isLightMode
            type="number"
            register={register(`${base}.benchmarkPlayerCount` as const, {
              setValueAs: value => (value === '' ? null : Number(value)),
            })}
            className={inputStateClass(
              fieldStateFor(issues, `${base}.benchmarkPlayerCount`)
            )}
            errorMessage={messageAt(issues, `${base}.benchmarkPlayerCount`)}
          />
          <TextInput
            label="'Too many players' threshold"
            isLightMode
            type="number"
            register={register(`${base}.tooManyPlayersAlert` as const, {
              setValueAs: value => (value === '' ? null : Number(value)),
            })}
            className={inputStateClass(
              fieldStateFor(issues, `${base}.tooManyPlayersAlert`)
            )}
            errorMessage={messageAt(issues, `${base}.tooManyPlayersAlert`)}
          />
        </div>
        <p className="mt-3 text-sm text-primary-500">
          Operating window — the hours the pitch is <em>open</em>. The form asks
          when out-of-hours alerting starts and ends, which is the inverse, so
          these are already swapped. Sanity-check them.
        </p>
        <div className="mt-2 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <TextInput
            label="Opens (hhmm)"
            isLightMode
            value={operatingWindow.start}
            onChange={value => setOperatingWindow('start', value)}
            className={inputStateClass(
              windowIssues.length > 0 ? 'invalid' : 'neutral'
            )}
            errorMessage={windowIssues[0]?.message}
          />
          <TextInput
            label="Closes (hhmm)"
            isLightMode
            value={operatingWindow.end}
            onChange={value => setOperatingWindow('end', value)}
            className={inputStateClass(
              windowIssues.length > 0 ? 'invalid' : 'neutral'
            )}
          />
        </div>
        <p className="mt-1 text-sm text-primary-500">
          Applies to every day — editing either time writes it to all seven.
        </p>
      </section>

      <section className="mt-6">
        <p className="text-lg font-semibold">Scheduled snapshots</p>
        <div className="mt-2 flex flex-col gap-2">
          {snapshotTimes.map((_, timeIndex) => (
            <div
              // controlled above, so the index is a safe key even as rows are removed
              key={`${base}-snapshot-${timeIndex}`}
              className="flex items-end gap-2"
            >
              <TextInput
                label={`Time ${timeIndex + 1}`}
                isLightMode
                size="small"
                value={snapshotTimes[timeIndex]}
                onChange={value =>
                  setSnapshotTimes(
                    snapshotTimes.map((time, index) =>
                      index === timeIndex ? value : time
                    )
                  )
                }
                className={inputStateClass(
                  fieldStateFor(issues, `${base}.snapshotTimes.${timeIndex}`)
                )}
                errorMessage={messageAt(
                  issues,
                  `${base}.snapshotTimes.${timeIndex}`
                )}
              />
              <Button
                text="✕"
                size="xsmall"
                type={ButtonType.Outline}
                onClick={() =>
                  setSnapshotTimes(
                    snapshotTimes.filter((__, index) => index !== timeIndex)
                  )
                }
              />
            </div>
          ))}
        </div>
        <div className="mt-2">
          <Button
            text="＋ Add snapshot time"
            size="xsmall"
            type={ButtonType.Outline}
            onClick={() => setSnapshotTimes([...snapshotTimes, '12:00:00'])}
          />
        </div>
      </section>

      <section className="mt-6">
        <div className="flex items-center justify-between">
          <p className="text-lg font-semibold">Sensors</p>
          <Button
            text="＋ Add sensor"
            size="xsmall"
            type={ButtonType.Outline}
            onClick={() =>
              appendSensor({
                id: null,
                latLong: pitch?.latLong ?? null,
                lensSpecMillimeters: null,
                perspectiveTransform: null,
              })
            }
          />
        </div>
        {issuesUnder(issues, `${base}.sensors`).length > 0 &&
          sensorFields.length === 0 && (
            <p className="mt-1 text-sm text-red-dark">
              A pitch needs at least one sensor.
            </p>
          )}
        <div className="mt-3 flex flex-col gap-4">
          {sensorFields.map((field, sensorIndex) => (
            <OnboardingSensorRow
              key={field.id}
              pitchIndex={pitchIndex}
              sensorIndex={sensorIndex}
              issues={issues}
              canRemove={sensorFields.length > 1}
              onRemove={() => removeSensor(sensorIndex)}
            />
          ))}
        </div>
      </section>

      <section className="mt-6">
        <p className="text-lg font-semibold">Installation</p>
        <div className="mt-2 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <TextInput
            label="Pitch commissioner"
            isLightMode
            register={register(
              `${base}.installDetails.pitchCommissioner` as const
            )}
          />
          <TextInput
            label="Installing contractor"
            isLightMode
            register={register(
              `${base}.installDetails.installingContractor` as const
            )}
          />
          <TextInput
            label="Pitch owner"
            isLightMode
            register={register(`${base}.installDetails.pitchOwner` as const)}
          />
          <TextInput
            label="Installation completed"
            isLightMode
            type="date"
            register={register(
              `${base}.installDetails.pitchInstallationDate` as never
            )}
          />
          <TextInput
            label="Latest resurface"
            isLightMode
            type="date"
            register={register(
              `${base}.installDetails.latestResurfaceDate` as never
            )}
          />
          <TextInput
            label="Turf maintenance specialist"
            isLightMode
            register={register(
              `${base}.installDetails.externalTurfMaintenanceSpecialist` as const
            )}
          />
        </div>
      </section>

      <section className="mt-6">
        <OnboardingMaintenanceSchedules
          pitchIndex={pitchIndex}
          issues={issues}
          templateSignatures={templateSignatures}
        />
      </section>
    </div>
  );
};
